/**
 * VoiceControls — Ambient visit recording with real-time transcription.
 *
 * Primary:  Deepgram Nova-3 Medical (WebSocket streaming, PCM16, diarization)
 * Fallback: Browser Web Speech API (if Deepgram key not configured)
 *
 * Provides:
 *   - Real-time transcript chunks sent to backend on every final result
 *   - Interim (in-progress) text passed via onInterim callback for live display
 *   - Speaker diarization: speaker 0 = Physician, speaker 1 = Patient
 *   - Recording timer
 *   - Pause / Resume / Stop
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Mic, Pause, Play, Square, Zap, MessageSquare, X, Check, RefreshCw, Radio } from 'lucide-react';
import { toast } from 'sonner';

const STATE_DISPLAY = {
  idle:         { label: '',             color: '',                animate: false },
  listening:    { label: 'Recording',    color: 'bg-red-500',      animate: true  },
  paused:       { label: 'Paused',       color: 'bg-amber-500',    animate: false },
  reconnecting: { label: 'Reconnecting', color: 'bg-blue-500',     animate: true  },
  error:        { label: 'Error',        color: 'bg-red-500',      animate: false },
  stopped:      { label: 'Stopped',      color: 'bg-slate-400',    animate: false },
};

const COMMAND_TIMEOUT_MS = 5000;

// ── Deepgram WebSocket Provider ──────────────────────────────────────────────

async function createDeepgramProvider({ onResult, onInterim, onError, onConnected }) {
  // Get key from backend (keeps API key server-side)
  let dgToken;
  try {
    const tokenRes = await api.get('/dictation/deepgram-token');
    dgToken = tokenRes.data.token;
  } catch (err) {
    const msg = err.response?.data?.detail || 'Deepgram not configured';
    throw new Error(msg);
  }

  // Request microphone
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      sampleRate: 16000,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });

  // Deepgram streaming URL — nova-3-medical + diarization + interim
  const dgUrl = [
    'wss://api.deepgram.com/v1/listen',
    '?model=nova-3-medical',
    '&language=en',
    '&smart_format=true',
    '&punctuate=true',
    '&diarize=true',
    '&interim_results=true',
    '&endpointing=500',
    '&encoding=linear16',
    '&sample_rate=16000',
    '&channels=1',
  ].join('');

  const ws = new WebSocket(dgUrl, ['token', dgToken]);

  let audioContext = null;
  let processorNode = null;
  let sourceNode = null;
  let active = true;

  ws.onopen = () => {
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      sourceNode = audioContext.createMediaStreamSource(stream);

      // ScriptProcessorNode: converts float32 → int16 PCM and sends to Deepgram
      processorNode = audioContext.createScriptProcessor(4096, 1, 1);
      processorNode.onaudioprocess = (e) => {
        if (!active || ws.readyState !== WebSocket.OPEN) return;
        const input = e.inputBuffer.getChannelData(0);
        const pcm16 = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
          pcm16[i] = Math.max(-32768, Math.min(32767, Math.round(input[i] * 32767)));
        }
        ws.send(pcm16.buffer);
      };

      sourceNode.connect(processorNode);
      processorNode.connect(audioContext.destination);
      if (onConnected) onConnected();
    } catch (e) {
      onError('audio_setup_failed');
    }
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.type !== 'Results') return;

      const alt = data.channel?.alternatives?.[0];
      if (!alt) return;

      const text = (alt.transcript || '').trim();
      if (!text) return;

      // Determine dominant speaker from words array
      // Speaker 0 = first speaker = Physician, Speaker 1 = Patient
      const words = alt.words || [];
      const speakerNum = words.length > 0 ? words[0].speaker ?? null : null;

      if (data.is_final) {
        onResult(text, 1.0, speakerNum);
      } else {
        if (onInterim) onInterim(text, speakerNum);
      }
    } catch (e) {
      // Ignore malformed messages
    }
  };

  ws.onerror = () => {
    if (active) onError('deepgram_websocket_error');
  };

  ws.onclose = () => {
    // Cleanup handled by stop()
  };

  return {
    type: 'deepgram',
    stop: () => {
      active = false;
      try { if (processorNode) { processorNode.disconnect(); } } catch {}
      try { if (sourceNode) { sourceNode.disconnect(); } } catch {}
      try { if (audioContext && audioContext.state !== 'closed') { audioContext.close(); } } catch {}
      try {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'CloseStream' }));
        }
        ws.close();
      } catch {}
      stream.getTracks().forEach(t => t.stop());
    },
  };
}

// ── Web Speech API Fallback ───────────────────────────────────────────────────

function createBrowserSpeechProvider({ onResult, onError }) {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) return null;

  const recognition = new SpeechRecognition();
  recognition.continuous = true;
  recognition.interimResults = false;
  recognition.lang = 'en-US';

  recognition.onresult = (event) => {
    const last = event.results[event.results.length - 1];
    if (!last.isFinal) return;
    onResult(last[0].transcript.trim(), last[0].confidence, null);
  };

  recognition.onerror = (event) => {
    if (event.error === 'no-speech' || event.error === 'aborted') return;
    onError(event.error);
  };

  let shouldLoop = false;
  recognition.onend = () => {
    if (shouldLoop) {
      try { recognition.start(); } catch {}
    }
  };

  return {
    type: 'browser',
    start: () => { shouldLoop = true; try { recognition.start(); } catch {} },
    stop: () => { shouldLoop = false; try { recognition.stop(); } catch {} },
  };
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function VoiceControls({ encounterId, onTranscriptChunk, onInterim, onCommand }) {
  const [session, setSession] = useState(null);
  const [pendingCommand, setPendingCommand] = useState(null);
  const [commandCountdown, setCommandCountdown] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [providerType, setProviderType] = useState(null); // 'deepgram' | 'browser'

  const providerRef = useRef(null);
  const countdownRef = useRef(null);
  const timerRef = useRef(null);

  // Server-authoritative state
  const voiceState = session?.state || 'idle';
  const voiceMode  = session?.mode  || 'command';
  const display    = STATE_DISPLAY[voiceState] || STATE_DISPLAY.idle;

  const formatTime = (secs) =>
    `${Math.floor(secs / 60).toString().padStart(2, '0')}:${(secs % 60).toString().padStart(2, '0')}`;

  // Fetch existing session on mount
  useEffect(() => {
    api.get(`/voice/${encounterId}/session`).then(res => {
      const s = res.data.session;
      if (s && s.state !== 'stopped') setSession(s);
    }).catch(() => {});
  }, [encounterId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (providerRef.current) providerRef.current.stop();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const executeCommand = useCallback(async (cmd) => {
    await api.post(`/voice/${encounterId}/command-lifecycle`, {
      event_type: 'command_executed', command_id: cmd.command_id,
      action: cmd.action, raw_text: cmd.raw_text, confidence: cmd.confidence,
    }).catch(() => {});
    if (onCommand) onCommand(cmd);
  }, [encounterId, onCommand]);

  const handleSpeechResult = useCallback(async (text, confidence, speaker) => {
    if (!text) return;

    // Send chunk to backend — include speaker label for diarized view
    try {
      await api.post(`/voice/${encounterId}/chunk`, {
        text,
        source: 'voice',
        confidence,
        speaker: speaker !== null && speaker !== undefined ? speaker : undefined,
      });
      if (onTranscriptChunk) onTranscriptChunk({ text, source: 'voice', confidence, speaker });
    } catch (err) {
      if (err.response?.status !== 400) console.error('Chunk send failed', err);
    }

    // Clear interim on final result
    if (onInterim) onInterim('', null);

    // Evaluate voice command
    try {
      const res = await api.post(`/voice/${encounterId}/evaluate-command`, { text });
      if (res.data.command) {
        const cmd = res.data.command;
        if (cmd.requires_confirmation) {
          setPendingCommand(cmd);
          setCommandCountdown(COMMAND_TIMEOUT_MS / 1000);
        } else {
          executeCommand(cmd);
        }
      }
    } catch {}
  }, [encounterId, onTranscriptChunk, onInterim, executeCommand]);

  const handleSpeechError = useCallback((error) => {
    console.error('Speech error:', error);
    api.post(`/voice/${encounterId}/transition`, { target_state: 'error', reason: error }).catch(() => {});
  }, [encounterId]);

  const handleInterim = useCallback((text, speaker) => {
    if (onInterim) onInterim(text, speaker);
  }, [onInterim]);

  // ── Start ────────────────────────────────────────────────────────────────────

  const startListening = async () => {
    try {
      const res = await api.post(`/voice/${encounterId}/start`, {});
      setSession(res.data.session);

      setElapsedSeconds(0);
      timerRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);

      // Try Deepgram first
      let provider = null;
      try {
        provider = await createDeepgramProvider({
          onResult: handleSpeechResult,
          onInterim: handleInterim,
          onError: handleSpeechError,
          onConnected: () => {
            setProviderType('deepgram');
          },
        });
        setProviderType('deepgram');
      } catch (dgErr) {
        // Fall back to browser Web Speech API
        console.warn('Deepgram unavailable, using browser STT:', dgErr.message);
        provider = createBrowserSpeechProvider({
          onResult: handleSpeechResult,
          onError: handleSpeechError,
        });
        if (provider) {
          provider.start();
          setProviderType('browser');
          toast.info('Using browser speech recognition (configure Deepgram in Settings for medical-grade STT)');
        } else {
          toast.error('No speech recognition available in this browser');
          clearInterval(timerRef.current);
          return;
        }
      }

      providerRef.current = provider;
    } catch (err) {
      clearInterval(timerRef.current);
      toast.error(err.response?.data?.detail || 'Failed to start voice session');
    }
  };

  const pauseListening = async () => {
    // Always stop audio first — never leave mic open on error
    if (timerRef.current) clearInterval(timerRef.current);
    if (providerRef.current?.type === 'browser' && providerRef.current.stop) {
      providerRef.current.stop();
    }
    try {
      const res = await api.post(`/voice/${encounterId}/transition`, { target_state: 'paused' });
      setSession(res.data.session);
    } catch (err) {
      // Optimistically mark paused in UI even if backend fails — mic is already stopped
      setSession(prev => prev ? { ...prev, state: 'paused' } : prev);
      if (err.response?.status !== 400) toast.warning('Paused (sync issue — will retry on resume)');
    }
  };

  const resumeListening = async () => {
    try {
      const res = await api.post(`/voice/${encounterId}/transition`, { target_state: 'listening' });
      setSession(res.data.session);
      timerRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
      if (providerRef.current?.type === 'browser' && providerRef.current.start) {
        providerRef.current.start();
      }
    } catch { toast.error('Failed to resume'); }
  };

  const stopListening = async () => {
    // Always clean up audio resources first — never leave mic open on error
    if (timerRef.current) clearInterval(timerRef.current);
    if (providerRef.current) {
      try { providerRef.current.stop(); } catch {}
      providerRef.current = null;
    }
    if (onInterim) onInterim('', null);
    setProviderType(null);

    try {
      const res = await api.post(`/voice/${encounterId}/transition`, { target_state: 'stopped' });
      setSession(res.data.session);
    } catch (err) {
      // Backend failed but mic is already stopped — reset UI to idle
      setSession(prev => prev ? { ...prev, state: 'stopped' } : { state: 'stopped' });
      if (err.response?.status !== 400) toast.warning('Recording stopped (could not sync state to server)');
    }
  };

  const toggleMode = async () => {
    const newMode = voiceMode === 'command' ? 'ambient' : 'command';
    try {
      const res = await api.post(`/voice/${encounterId}/mode`, { mode: newMode });
      setSession(res.data.session);
    } catch { toast.error('Failed to switch mode'); }
  };

  const dismissCommand = useCallback(async () => {
    if (pendingCommand) {
      await api.post(`/voice/${encounterId}/command-lifecycle`, {
        event_type: 'command_dismissed', command_id: pendingCommand.command_id,
        action: pendingCommand.action, raw_text: pendingCommand.raw_text,
      }).catch(() => {});
    }
    setPendingCommand(null);
  }, [pendingCommand, encounterId]);

  const confirmCommand = async () => {
    if (!pendingCommand) return;
    await api.post(`/voice/${encounterId}/command-lifecycle`, {
      event_type: 'command_confirmed', command_id: pendingCommand.command_id,
      action: pendingCommand.action, raw_text: pendingCommand.raw_text, confidence: pendingCommand.confidence,
    }).catch(() => {});
    executeCommand(pendingCommand);
    setPendingCommand(null);
  };

  // Command timeout countdown
  useEffect(() => {
    if (!pendingCommand) {
      if (countdownRef.current) clearInterval(countdownRef.current);
      return;
    }
    setCommandCountdown(COMMAND_TIMEOUT_MS / 1000);
    countdownRef.current = setInterval(() => {
      setCommandCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownRef.current); dismissCommand(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [pendingCommand, dismissCommand]);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <TooltipProvider delayDuration={200}>
      <div data-testid="voice-controls">

        {/* Recording status bar — only visible while recording */}
        {voiceState === 'listening' && (
          <div className="flex items-center gap-2.5 px-4 py-1.5 bg-red-600 text-white text-xs shrink-0"
            data-testid="recording-banner">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span className="font-medium">Recording</span>
            <span className="font-mono tabular-nums">{formatTime(elapsedSeconds)}</span>
            {providerType === 'deepgram' && (
              <Badge className="text-[9px] bg-red-700 text-white border-red-500 gap-0.5 ml-1">
                <Radio className="w-2 h-2" /> Deepgram Medical
              </Badge>
            )}
            {providerType === 'browser' && (
              <Badge className="text-[9px] bg-red-800 text-white border-red-600 gap-0.5 ml-1">
                Browser STT
              </Badge>
            )}
          </div>
        )}
        {voiceState === 'paused' && (
          <div className="flex items-center gap-2.5 px-4 py-1.5 bg-amber-500 text-white text-xs shrink-0">
            <span className="w-2 h-2 rounded-full bg-white" />
            <span className="font-medium">Paused</span>
            <span className="font-mono tabular-nums text-amber-100">{formatTime(elapsedSeconds)}</span>
          </div>
        )}

        {/* Voice Control Strip */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-b bg-slate-50/80 shrink-0" data-testid="voice-control-strip">

          {voiceState === 'idle' || voiceState === 'stopped' ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs text-emerald-700 hover:bg-emerald-50 px-2"
                  onClick={startListening} data-testid="voice-start-btn">
                  <Mic className="w-3.5 h-3.5" /> Start Recording
                </Button>
              </TooltipTrigger>
              <TooltipContent className="text-[10px]">Record visit audio for transcript</TooltipContent>
            </Tooltip>
          ) : voiceState === 'listening' ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-amber-50" onClick={pauseListening} data-testid="voice-pause-btn">
                    <Pause className="w-3.5 h-3.5 text-amber-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="text-[10px]">Pause recording</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-red-50" onClick={stopListening} data-testid="voice-stop-btn">
                    <Square className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="text-[10px]">Stop recording</TooltipContent>
              </Tooltip>
            </>
          ) : voiceState === 'paused' ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-emerald-50" onClick={resumeListening} data-testid="voice-resume-btn">
                    <Play className="w-3.5 h-3.5 text-emerald-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="text-[10px]">Resume recording</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-red-50" onClick={stopListening} data-testid="voice-stop-btn">
                    <Square className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="text-[10px]">Stop recording</TooltipContent>
              </Tooltip>
            </>
          ) : voiceState === 'error' ? (
            <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1 text-red-500" onClick={startListening} data-testid="voice-retry-btn">
              <RefreshCw className="w-3 h-3" /> Retry
            </Button>
          ) : null}

          {(voiceState === 'listening' || voiceState === 'paused') && (
            <>
              <div className="w-px h-4 bg-slate-200 mx-1" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm"
                    className={`h-7 text-[10px] gap-1 px-2 ${voiceMode === 'command' ? 'text-blue-600' : 'text-slate-500'}`}
                    onClick={toggleMode} data-testid="voice-mode-toggle">
                    {voiceMode === 'command' ? <Zap className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                    {voiceMode === 'command' ? 'Command' : 'Ambient'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="text-[10px]">
                  {voiceMode === 'command' ? 'Switch to Ambient (transcript only)' : 'Switch to Command (voice commands active)'}
                </TooltipContent>
              </Tooltip>
              {session?.chunk_count > 0 && (
                <Badge variant="outline" className="text-[9px] h-4 px-1 ml-auto tabular-nums">{session.chunk_count} chunks</Badge>
              )}
            </>
          )}
        </div>

        {/* Command confirmation banner */}
        {pendingCommand && (
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-blue-200 shrink-0" data-testid="voice-command-confirmation">
            <Zap className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            <span className="text-xs text-blue-800 flex-1">
              Command: <span className="font-semibold">{pendingCommand.description}</span>
            </span>
            <span className="text-[10px] text-blue-500 tabular-nums w-4 text-center">{commandCountdown}s</span>
            <Button size="sm" className="h-6 text-[10px] gap-1 bg-blue-600 hover:bg-blue-700" onClick={confirmCommand} data-testid="voice-command-confirm">
              <Check className="w-3 h-3" /> Confirm
            </Button>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-600" onClick={dismissCommand} data-testid="voice-command-dismiss">
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
